import { Box } from "@mui/material";
import {
  AccountBalanceWallet,
  Savings,
  AccountBalance,
  ShowChart,
  TrendingUp,
  Percent,
} from "@mui/icons-material";
import { DashboardMetricCard } from "./DashboardMetricCard";
import { useResponsive } from "../../../core/hooks/useResponsive";

function formatInr(n, compact = false) {
  if (n == null || n === "") return "—";
  const num = typeof n === "string" ? parseFloat(n) : Number(n);
  if (Number.isNaN(num)) return String(n);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    // Paise are noise on a 160px-wide phone tile; the hero tile keeps full precision.
    maximumFractionDigits: compact ? 0 : 2,
  }).format(num);
}

function formatPct(n) {
  if (n == null || n === "") return "—";
  const num = typeof n === "string" ? parseFloat(n) : Number(n);
  if (Number.isNaN(num)) return String(n);
  return `${num.toFixed(2)}%`;
}

function signedPct(n) {
  const num = typeof n === "string" ? parseFloat(n) : Number(n);
  if (!Number.isFinite(num)) return "—";
  const arrow = num > 0 ? "▲" : num < 0 ? "▼" : "•";
  return `${arrow} ${Math.abs(num).toFixed(2)}%`;
}

/**
 * Cash-flow metrics — same `data` shape as API `cashFlow`.
 *
 * Layout by width:
 * - phone      Net P/L becomes a full-width hero tile with Growth folded in as a pill
 *              (they're the same fact), then the four balance figures 2-up. Six stacked
 *              full-height cards used to cost ~510px of scroll; this is ~225px.
 * - 480-1023   all six as dense tiles, 3-up
 * - >=1024     original six-across row
 */
export const CashFlow = ({ data }) => {
  const { isPhone, isCompact } = useResponsive();
  const d = data || {};
  const growth = parseFloat(d.plnPercentage);
  const pl = parseFloat(d.profitLoss);

  const getTrendAccent = (value) => {
    if (value > 0) return "success";
    if (value < 0) return "error";
    return "primary";
  };
  const getTrendColor = (value) => {
    if (value > 0) return "success.main";
    if (value < 0) return "error.main";
    return "text.primary";
  };
  const growthAccent = getTrendAccent(growth);
  const plAccent = getTrendAccent(pl);

  if (isPhone) {
    return (
      <Box sx={{ display: "grid", gap: 1 }}>
        <DashboardMetricCard
          hero
          label="Net P/L"
          value={formatInr(d.profitLoss)}
          badge={signedPct(d.plnPercentage)}
          badgeColor={
            growth > 0 ? "#16a34a" : growth < 0 ? "#dc2626" : undefined
          }
          sublabel="Growth on invested capital"
          icon={TrendingUp}
          accent={plAccent}
          valueColor={getTrendColor(pl)}
          highlight
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          <DashboardMetricCard dense label="Trade budget" value={formatInr(d.tradeBudget, true)} icon={AccountBalanceWallet} accent="primary" />
          <DashboardMetricCard dense label="Available" value={formatInr(d.balance, true)} icon={AccountBalance} accent="secondary" />
          <DashboardMetricCard dense label="Invested" value={formatInr(d.invested, true)} icon={Savings} accent="primary" />
          <DashboardMetricCard dense label="Holdings value" value={formatInr(d.marketValue, true)} icon={ShowChart} accent="primary" />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.25,
        gridTemplateColumns: {
          sm: "repeat(3, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
          lg: "repeat(6, minmax(0, 1fr))",
        },
      }}
    >
      <DashboardMetricCard dense={isCompact} label="Trade budget" value={formatInr(d.tradeBudget)} icon={AccountBalanceWallet} accent="primary" />
      <DashboardMetricCard dense={isCompact} label="Invested" value={formatInr(d.invested)} icon={Savings} accent="primary" />
      <DashboardMetricCard dense={isCompact} label="Available" value={formatInr(d.balance)} icon={AccountBalance} accent="secondary" />
      <DashboardMetricCard dense={isCompact} label="Holdings value" value={formatInr(d.marketValue)} icon={ShowChart} accent="primary" />
      <DashboardMetricCard
        dense={isCompact}
        label="Net P/L"
        value={formatInr(d.profitLoss)}
        icon={TrendingUp}
        accent={plAccent}
        valueColor={getTrendColor(pl)}
        highlight
      />
      <DashboardMetricCard
        dense={isCompact}
        label="Growth"
        value={formatPct(d.plnPercentage)}
        icon={Percent}
        accent={growthAccent}
        valueColor={getTrendColor(growth)}
        highlight
      />
    </Box>
  );
};
